import { useState, FormEvent, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackLeadGeneration } from '@/utils/analytics';

function detectProductInterest(param: string): string {
  const trimmed = param.trim();
  const lower = trimmed.toLowerCase();

  // Match by specific model codes first
  // A Series: starts with A followed by digit or dash (A6, A9, A12, A-S Elite, A-P3 Elite, etc.)
  if (/^a[\d-]/.test(lower)) return 'A Series';
  // X Series: X3, X5, X6, X7, Y6, V6
  if (/^x\d/.test(lower)) return 'X Series';
  if (/^y\d/.test(lower)) return 'X Series';
  if (/^v\d/.test(lower)) return 'X Series';
  // M Series: M5, M7, M9
  if (/^m\d/.test(lower)) return 'M Series';
  // Z Series: Z-prefixed models
  if (/^z[\d-]/.test(lower)) return 'Z Series';

  // Fallback: match by series name or range slug in param
  if (lower.includes('a series') || lower.includes('a-range')) return 'A Series';
  if (lower.includes('x series') || lower.includes('x-range')) return 'X Series';
  if (lower.includes('m series') || lower.includes('m-range')) return 'M Series';
  if (lower.includes('z series') || lower.includes('z-range')) return 'Z Series';

  return '';
}

const INPUT_CLASS = "w-full px-4 py-3 bg-black/50 border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all";

const SELECT_CLASS = "w-full px-4 py-3 bg-black/50 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all appearance-none cursor-pointer";

const LABEL_CLASS = "block text-sm font-medium text-gray-300 mb-2";

const REQUIRED_STAR = <span className="text-cyan-400 ml-0.5">*</span>;

export default function ContactForm() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productInterest: '',
    intendedUse: '',
    projectLocation: '',
    targetTimeline: '',
    indicativeBudget: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Auto-prefill from Request Quote link params (?model= or ?product=)
  useEffect(() => {
    const model = searchParams.get('model');
    const product = searchParams.get('product');
    const queryParam = model || product || '';
    const interest = detectProductInterest(queryParam);
    const label = model || product || '';

    if (label) {
      setFormData(prev => ({
        ...prev,
        subject: 'pricing',
        message: `I would like to request a quote for the ${label} model. Please provide pricing, availability and specification details.`,
        productInterest: interest || prev.productInterest,
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone || 'Not provided');
      formBody.append('company', formData.company || 'Not provided');
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);
      formBody.append('product_interest', formData.productInterest || 'Not specified');
      formBody.append('intended_use', formData.intendedUse || 'Not specified');
      formBody.append('project_location', formData.projectLocation || 'Not specified');
      formBody.append('target_timeline', formData.targetTimeline || 'Not specified');
      formBody.append('indicative_budget', formData.indicativeBudget || 'Not specified');

      const response = await fetch('/api/form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        trackLeadGeneration('contact_form');
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productInterest: '',
          intendedUse: '',
          projectLocation: '',
          targetTimeline: '',
          indicativeBudget: '',
          subject: '',
          message: ''
        });
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 7000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-[#0A0A0A] to-[#0D1117]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-[0_0_50px_rgba(0,217,255,0.1)]">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            Send Us a Message
          </h2>
          <p className="text-center text-gray-400 mb-8 sm:mb-10 lg:mb-12 text-base sm:text-lg">
            Fill out the form below and we&apos;ll get back to you within 24 hours
          </p>

          <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
            {/* honeypot: hidden from users, bots fill it and get silently dropped */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            {/* Row 1: Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className={LABEL_CLASS}>
                  Full Name {REQUIRED_STAR}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className={LABEL_CLASS}>
                  Email Address {REQUIRED_STAR}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Row 2: Phone + Company */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="phone" className={LABEL_CLASS}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="+61 4XX XXX XXX"
                />
              </div>

              <div>
                <label htmlFor="company" className={LABEL_CLASS}>
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="Your Company"
                />
              </div>
            </div>

            {/* Row 3: Product Interest + Intended Use */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="productInterest" className={LABEL_CLASS}>
                  Product Interest
                </label>
                <div className="relative">
                  <select
                    id="productInterest"
                    name="productInterest"
                    value={formData.productInterest}
                    onChange={handleChange}
                    className={SELECT_CLASS}
                  >
                    <option value="">Not sure yet</option>
                    <option value="A Series">A Series</option>
                    <option value="X Series">X Series</option>
                    <option value="M Series">M Series</option>
                    <option value="Z Series">Z Series</option>
                    <option value="Custom solution">Custom solution</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg"></i>
                </div>
              </div>

              <div>
                <label htmlFor="intendedUse" className={LABEL_CLASS}>
                  Intended Use
                </label>
                <div className="relative">
                  <select
                    id="intendedUse"
                    name="intendedUse"
                    value={formData.intendedUse}
                    onChange={handleChange}
                    className={SELECT_CLASS}
                  >
                    <option value="">Select intended use</option>
                    <option value="Backyard studio">Backyard studio</option>
                    <option value="Guest accommodation">Guest accommodation</option>
                    <option value="Short-stay / holiday park">Short-stay / holiday park</option>
                    <option value="Office / workspace">Office / workspace</option>
                    <option value="Property project">Property project</option>
                    <option value="Other">Other</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg"></i>
                </div>
              </div>
            </div>

            {/* Row 4: Project Location + Target Timeline */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="projectLocation" className={LABEL_CLASS}>
                  Project Location
                </label>
                <input
                  type="text"
                  id="projectLocation"
                  name="projectLocation"
                  value={formData.projectLocation}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                  placeholder="Suburb, state or postcode"
                />
              </div>

              <div>
                <label htmlFor="targetTimeline" className={LABEL_CLASS}>
                  Target Timeline
                </label>
                <div className="relative">
                  <select
                    id="targetTimeline"
                    name="targetTimeline"
                    value={formData.targetTimeline}
                    onChange={handleChange}
                    className={SELECT_CLASS}
                  >
                    <option value="">Select timeline</option>
                    <option value="As soon as possible">As soon as possible</option>
                    <option value="Within 3 months">Within 3 months</option>
                    <option value="3–6 months">3–6 months</option>
                    <option value="6–12 months">6–12 months</option>
                    <option value="Just researching">Just researching</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg"></i>
                </div>
              </div>
            </div>

            {/* Row 5: Indicative Budget + Subject */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="indicativeBudget" className={LABEL_CLASS}>
                  Indicative Budget
                </label>
                <div className="relative">
                  <select
                    id="indicativeBudget"
                    name="indicativeBudget"
                    value={formData.indicativeBudget}
                    onChange={handleChange}
                    className={SELECT_CLASS}
                  >
                    <option value="">Select budget range</option>
                    <option value="Under $50,000">Under $50,000</option>
                    <option value="$50,000–$100,000">$50,000–$100,000</option>
                    <option value="$100,000–$200,000">$100,000–$200,000</option>
                    <option value="$200,000+">$200,000+</option>
                    <option value="Not sure yet">Not sure yet</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg"></i>
                </div>
              </div>

              <div>
                <label htmlFor="subject" className={LABEL_CLASS}>
                  Subject {REQUIRED_STAR}
                </label>
                <div className="relative">
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className={SELECT_CLASS}
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="product">Product Information</option>
                    <option value="pricing">Pricing &amp; Quotation</option>
                    <option value="partnership">Partnership Opportunities</option>
                    <option value="support">Technical Support</option>
                    <option value="other">Other</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-lg"></i>
                </div>
              </div>
            </div>

            {/* Row 6: Message (full width) */}
            <div>
              <label htmlFor="message" className={LABEL_CLASS}>
                Message {REQUIRED_STAR}
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                maxLength={500}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-black/50 border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all resize-none"
                placeholder="Tell us about your project..."
              ></textarea>
            </div>

            {/* Submit + Privacy */}
            <div className="flex flex-col items-center gap-4 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-lg font-semibold rounded-full hover:from-cyan-400 hover:to-blue-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.5)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-xl"></i>
                    Send Message
                  </>
                )}
              </button>

              <p className="text-gray-500 text-sm text-center">
                <i className="ri-shield-check-line mr-1.5"></i>
                Your details are used only to respond to your enquiry.
              </p>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="flex items-center gap-2 text-green-400 bg-green-400/10 px-6 py-3 rounded-lg border border-green-400/30">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  <span>Message sent successfully! We&apos;ll get back to you soon.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-400 bg-red-400/10 px-6 py-3 rounded-lg border border-red-400/30">
                  <i className="ri-error-warning-fill text-xl"></i>
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
